import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, FileText, PlugZap, Sparkles } from 'lucide-react';
import { toolLogo } from './AppChrome';

/**
 * The brain is only interesting at the moment it answers something. So this
 * section is one conversation on the right, and the three things Giga read to
 * answer it on the left — observations, memory files, and the live tools —
 * joined by dashed rails. Tabs swap the scenario, not the structure.
 */

const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png';
type Scenario = {
  tab: string;
  who: string;
  initials: string;
  tint: string;
  question: string;
  observations: Array<{
    logo: string;
    title: string;
    detail: string;
  }>;
  files: Array<{
    path: string;
    note: string;
  }>;
  tools: Array<{
    logo: string;
    name: string;
    note: string;
  }>;
  trace: string[];
  answer: string[];
};
const SCENARIOS: Scenario[] = [{
  tab: 'A deal that went quiet',
  who: 'Priya · Growth',
  initials: 'PR',
  tint: '#7C2D12',
  question: 'Where did Northwind actually land?',
  observations: [{
    logo: 'granola',
    title: 'Northwind pilot moved to Q4',
    detail: 'Budget freeze on new spend — not a competitive loss.'
  }, {
    logo: 'slack',
    title: 'Champion unchanged',
    detail: 'Still Dana Alvarez, VP Ops. She asked to be re-engaged in Sep.'
  }],
  files: [{
    path: 'customers/northwind.md',
    note: 'Timeline, budget cycle, open risks'
  }],
  tools: [{
    logo: 'salesforce',
    name: 'Salesforce',
    note: 'Open · $84k · Legal review'
  }],
  trace: ['Matched 2 observations from the pilot review', 'Opened customers/northwind.md', 'Checked the live opportunity in Salesforce'],
  answer: ['Pushed to Q4, not lost. Their CFO froze new spend on the 14th, and the champion is unchanged — Dana asked to be re-engaged in September.', 'The opportunity is still open at $84k in Legal review, so the forecast should move rather than close.']
}, {
  tab: 'What customers are stuck on',
  who: 'Sarah · Ops',
  initials: 'SC',
  tint: '#134E4A',
  question: 'What are customers blocked on this month?',
  observations: [{
    logo: 'intercom',
    title: 'Setup is the top blocker',
    detail: '31 conversations named it, ahead of pricing and integrations.'
  }, {
    logo: 'linear',
    title: 'CSV import bug shipped',
    detail: 'Clears the blocker cited on two open opportunities.'
  }],
  files: [{
    path: 'product/onboarding.md',
    note: 'What we promise, where people get stuck'
  }, {
    path: 'accounts/renewals.md',
    note: 'Every renewal, owner and last contact'
  }],
  tools: [{
    logo: 'intercom',
    name: 'Intercom',
    note: '31 conversations this month'
  }],
  trace: ['Matched 2 observations across support and engineering', 'Opened 2 memory files', 'Counted live conversations in Intercom'],
  answer: ['Setup, by a wide margin — 31 support conversations named it this month, ahead of pricing and integrations.', 'The CSV import fix shipped Tuesday, which clears the blocker on the two deals that cited it. Onboarding is still quoted as 14 days, which is where the gap shows up.']
}, {
  tab: 'A rule someone agreed months ago',
  who: 'Tom · Finance',
  initials: 'TO',
  tint: '#1E3A5F',
  question: 'How much can I discount the Harbour renewal?',
  observations: [{
    logo: 'granola',
    title: 'Discount ceiling agreed at 15%',
    detail: 'Set on the March pricing call; replaces the case-by-case rule.'
  }, {
    logo: 'gmail',
    title: 'Harbour has gone quiet',
    detail: 'No reply for nine days on the renewal thread.'
  }],
  files: [{
    path: 'commercial/pricing-rules.md',
    note: 'Ceilings and who can approve them'
  }],
  tools: [{
    logo: 'salesforce',
    name: 'Salesforce',
    note: 'Harbour · renews 30 Sep'
  }],
  trace: ['Matched the pricing decision from March', 'Opened commercial/pricing-rules.md', 'Checked the renewal date in Salesforce'],
  answer: ['Up to 15% without approval. That ceiling was agreed on the March pricing call and replaced the old case-by-case rule.', 'Harbour renews on 30 September and has been quiet for nine days, so a call before the offer is probably worth more than the discount.']
}];
const SOURCE_META = [{
  key: 'observations' as const,
  label: 'Observations',
  icon: Sparkles,
  note: 'Extracted from conversations and hourly scans'
}, {
  key: 'files' as const,
  label: 'Memory files',
  icon: FileText,
  note: 'Plain markdown, appended to as things change'
}, {
  key: 'tools' as const,
  label: 'Connected tools',
  icon: PlugZap,
  note: 'Checked live at the moment of the question'
}];
export function CompanyBrainPillar() {
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();
  const scenario = SCENARIOS[index];
  return <div data-id="element-219" data-mp="93xesznoiqax">
      <div className="mx-auto max-w-3xl text-center" data-id="element-220" data-mp="93no19ut8f46">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-221" data-mp="1tlorc7oz5edw">
          Company Brain
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-222" data-mp="1okse33iqgfvt">
          Ask the company,
          <br data-id="element-223" data-mp="1flnhw3un8ymt" />
          not a colleague.
        </h2>
        <p className="mx-auto mt-5 max-w-[56ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-224" data-mp="1fn89plpkq3gz">
          Giga keeps its own record of how your company works — observations and
          memory files written as your team talks and as hourly scans sweep your
          systems. Every answer draws on those, plus a live look at your tools.
        </p>
      </div>

      {/* Tabs are scenarios, so the same architecture answers three very
       different questions. */}
      <div className="mx-auto mt-10 flex max-w-[1000px] flex-wrap justify-center gap-2" data-id="element-225" data-mp="2w0faagrsi5w4">
        {SCENARIOS.map((s, i) => <button key={s.tab} type="button" onClick={() => setIndex(i)} aria-pressed={i === index} className={`rounded-full border px-4 py-2 text-[13.5px] font-semibold transition-colors ${i === index ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#E7E0D4] bg-white text-[#4A3E35] hover:border-[#2B211A]/40'}`} data-id="element-226" data-mp="2r85nvapvvnyl" data-mpi={i}>
            {s.tab}
          </button>)}
      </div>

      <div className="mx-auto mt-8 grid max-w-[1080px] gap-4 lg:grid-cols-[minmax(0,0.86fr)_72px_minmax(0,1.14fr)] lg:gap-0" data-id="element-227" data-mp="35eh3h45nbae2">
        {/* Sources */}
        <div className="space-y-3" data-id="element-228" data-mp="2rdd1knycgk4y">
          {SOURCE_META.map(meta => <SourceCard key={meta.key} meta={meta} scenario={scenario} scenarioKey={scenario.tab} data-id="element-229" data-mp="2weka4p8b9nco" />)}
        </div>

        <DashedRails animate={!reduce} data-id="element-230" data-mp="2ifb24cu9dzjs" />

        <ChatPanel scenario={scenario} data-id="element-231" data-mp="2igci8vq4tc8d" />
      </div>

      <p className="mx-auto mt-8 max-w-[1080px] text-center text-[15px] font-medium text-[#6B5D52]" data-id="element-232" data-mp="2mgwbho7l3vp8">
        Nobody wrote a wiki page for any of that. The company worked, and the
        brain kept up.
      </p>
    </div>;
}
const CARD_H = 148;
const CARD_GAP = 12;
function SourceCard({
  meta,
  scenario,
  scenarioKey
}: {
  meta: (typeof SOURCE_META)[number];
  scenario: Scenario;
  scenarioKey: string;
}) {
  const Icon = meta.icon;
  const count = meta.key === 'observations' ? scenario.observations.length : meta.key === 'files' ? scenario.files.length : scenario.tools.length;
  return <div className="flex flex-col overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white p-4 lg:h-[148px]" style={{
    minHeight: CARD_H
  }} data-id="element-233" data-mp="2a5dewmpclb1l">
      <div className="flex items-center justify-between gap-2" data-id="element-234" data-mp="2owm9zbc0rom4">
        <span className="flex items-center gap-2" data-id="element-235" data-mp="2jun9qabkt7te">
          <span className="flex size-7 items-center justify-center rounded-lg bg-[#FFF1E7] text-[#EA580C]" data-id="element-236" data-mp="2xv7v9e8vlhql">
            <Icon size={14} strokeWidth={2.1} data-id="element-237" data-mp="2swd2bjh72csr" />
          </span>
          <span className="font-title text-[15px] font-bold tracking-tight text-[#2B211A]" data-id="element-238" data-mp="2su0jl43m3yen">
            {meta.label}
          </span>
        </span>
        <span className="shrink-0 rounded-full bg-[#F4F0E9] px-2 py-0.5 text-[10.5px] font-bold text-[#6B5D52]" data-id="element-239" data-mp="yivlefbaoe6p">
          {count} used
        </span>
      </div>

      <AnimatePresence mode="wait" data-id="element-240" data-mp="dkl9x6zxua6z">
        <motion.div key={scenarioKey} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-2.5 flex-1 space-y-1.5" data-id="element-241" data-mp="hn7uqny6yuuo">
          {meta.key === 'observations' && scenario.observations.map(o => <div key={o.title} className="rounded-xl border border-[#EFEAE1] px-2.5 py-2" data-id="element-242" data-mp="3qvaiglzia915">
                <span className="flex items-center gap-2" data-id="element-243" data-mp="3uwcmrh8a5b9z">
                  <img src={toolLogo(o.logo)} alt="" aria-hidden="true" className="size-3.5 shrink-0 object-contain" data-id="element-244" data-mp="3ngoxahtwkrr" />
                  <span className="truncate text-[12.5px] font-semibold text-[#2B211A]" data-id="element-245" data-mp="8omj7cr50g3e">
                    {o.title}
                  </span>
                </span>
                <span className="mt-0.5 block truncate text-[11.5px] text-[#6B5D52]" data-id="element-246" data-mp="1k1f6aa8zggd4">
                  {o.detail}
                </span>
              </div>)}

          {meta.key === 'files' && scenario.files.map(f => <div key={f.path} className="rounded-xl border border-[#EFEAE1] px-2.5 py-2" data-id="element-247" data-mp="2zmh63yxlfwbz">
                <span className="block truncate font-mono text-[12px] font-semibold text-[#2B211A]" data-id="element-248" data-mp="2utnzt6mh8fxs">
                  {f.path}
                </span>
                <span className="mt-0.5 block truncate text-[11.5px] text-[#6B5D52]" data-id="element-249" data-mp="2pts6q2fbi8x0">
                  {f.note}
                </span>
              </div>)}

          {meta.key === 'tools' && scenario.tools.map(t => <div key={t.name} className="rounded-xl border border-[#EFEAE1] px-2.5 py-2" data-id="element-250" data-mp="2gxatmtne80ym">
                <span className="flex items-center gap-2" data-id="element-251" data-mp="2bxohyon7qsrx">
                  <img src={toolLogo(t.logo)} alt="" aria-hidden="true" className="size-3.5 shrink-0 object-contain" data-id="element-252" data-mp="26y23kbvnf6hj" />
                  <span className="truncate text-[12.5px] font-semibold text-[#2B211A]" data-id="element-253" data-mp="a3p6o3a3cbf8">
                    {t.name}
                  </span>
                </span>
                <span className="mt-0.5 block truncate text-[11.5px] text-[#6B5D52]" data-id="element-254" data-mp="1k29e5egomwjw">
                  {t.note}
                </span>
              </div>)}
        </motion.div>
      </AnimatePresence>

      <p className="mt-2 truncate text-[11px] text-neutral-400" data-id="element-255" data-mp="1u1i8i8fgmd6s">{meta.note}</p>
    </div>;
}

/**
 * Dashed rails from each source card into the conversation. Card heights are
 * fixed on desktop, so the geometry can be derived from indices instead of a
 * measurement pass.
 */
function DashedRails({
  animate
}: {
  animate: boolean;
}) {
  const height = 3 * CARD_H + 2 * CARD_GAP;
  const target = height / 2;
  const width = 72;
  return <div className="hidden lg:block" aria-hidden="true" style={{
    height
  }} data-id="element-256" data-mp="1b3p5z78r059x">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-id="element-257" data-mp="16aw20ll3t0yw">
        {[0, 1, 2].map(i => {
        const y = i * (CARD_H + CARD_GAP) + CARD_H / 2;
        const d = `M0 ${y} C ${width * 0.5} ${y}, ${width * 0.5} ${target}, ${width} ${target}`;
        return animate ? <motion.path key={i} d={d} fill="none" stroke="#D8CFC0" strokeWidth={1.4} strokeDasharray="3 5" initial={{
          strokeDashoffset: 0
        }} animate={{
          strokeDashoffset: -32
        }} transition={{
          duration: 1.6,
          ease: 'linear',
          repeat: Infinity,
          delay: i * 0.18
        }} data-id="element-258" data-mp="2pa2d73mjre2l" /> : <path key={i} d={d} fill="none" stroke="#D8CFC0" strokeWidth={1.4} strokeDasharray="3 5" data-id="element-259" data-mp="2kbileml9v82b" />;
      })}
      </svg>
    </div>;
}
function ChatPanel({
  scenario
}: {
  scenario: Scenario;
}) {
  return <div className="flex flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] bg-white shadow-[0_40px_100px_-46px_rgba(43,33,26,0.5)]" style={{
    minHeight: 3 * CARD_H + 2 * CARD_GAP
  }} data-id="element-260" data-mp="3cz4yclge3p1u">
      <div className="flex items-center gap-2 border-b border-[#EFEAE1] px-5 py-3" data-id="element-261" data-mp="333tly9sgetx9">
        <span className="flex size-5 items-center justify-center rounded-md bg-[#EA580C]" data-id="element-262" data-mp="1njf49wwa2md4">
          <img src={GIGA_MARK} alt="" className="size-2.5 [filter:brightness(0)_invert(1)]" data-id="element-263" data-mp="1ro4xfdzjxmuc" />
        </span>
        <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-264" data-mp="3u8h1ih3ud5qa">
          Brightwave
        </span>
        <span className="text-[12px] text-neutral-400" data-id="element-265" data-mp="3paxlpcrhwkw8">· company brain</span>
      </div>

      <AnimatePresence mode="wait" data-id="element-266" data-mp="3khusj5qmrdgk">
        <motion.div key={scenario.question} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex-1 px-5 py-5" data-id="element-267" data-mp="3fkcfb35kyc2r">
          <div className="flex justify-end" data-id="element-268" data-mp="31kbj4109fstm">
            <div className="max-w-[86%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5" data-id="element-269" data-mp="31l3at0qql6em">
              <p className="text-[14px] font-medium text-[#2B211A]" data-id="element-270" data-mp="2wn1hzo3idspy">
                {scenario.question}
              </p>
            </div>
          </div>
          <p className="mt-1.5 flex items-center justify-end gap-1.5 text-[11px] text-neutral-400" data-id="element-271" data-mp="uuddlbf4oz8l">
            <span className="flex size-4 items-center justify-center rounded-full text-[7.5px] font-bold text-white" style={{
            backgroundColor: scenario.tint
          }} data-id="element-272" data-mp="13be1mhcx2ewd">
              {scenario.initials}
            </span>
            {scenario.who}
          </p>

          {/* The trace is the proof: three named reads before a word of the
           answer, each one matching a card on the left. */}
          <ul className="mt-5 space-y-1.5" data-id="element-273" data-mp="kd1pqnumeqx3">
            {scenario.trace.map((t, i) => <motion.li key={t} initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.24,
            delay: 0.05 + i * 0.06,
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-center gap-2 text-[12.5px] text-[#6B5D52]" data-id="element-274" data-mp="odlkjkrq1ig2" data-mpi={i}>
                <Check size={12} className="shrink-0 text-emerald-600" data-id="element-275" data-mp="24rv9afzvl97f" />
                {t}
              </motion.li>)}
          </ul>

          <div className="mt-4 flex items-start gap-3" data-id="element-276" data-mp="3io50elqfge4n">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-277" data-mp="3iooh5z4ccwh3">
              <img src={GIGA_MARK} alt="" className="size-3.5 [filter:brightness(0)_invert(1)]" data-id="element-278" data-mp="3dqweqb62lryn" />
            </span>
            <div className="min-w-0 space-y-2.5" data-id="element-279" data-mp="3rmqdw2h1l2yk">
              {scenario.answer.map(p => <p key={p} className="text-[14.5px] leading-relaxed text-[#3D3229]" data-id="element-280" data-mp="3mo6ry22g7r67">
                  {p}
                </p>)}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="border-t border-[#EFEAE1] px-5 py-3.5" data-id="element-281" data-mp="i0ba71gw5ad">
        <div className="flex items-center gap-2 rounded-full border border-[#E7E0D4] px-4 py-2" data-id="element-282" data-mp="ea6hb0awovw3">
          <span className="flex-1 text-[13px] text-neutral-400" data-id="element-283" data-mp="a833i50rlf7n">
            Ask anything about your company
          </span>
          <span className="flex size-6 items-center justify-center rounded-full bg-[#2B211A] text-white" data-id="element-284" data-mp="o8vysryw4ebt">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true" data-id="element-285" data-mp="jaksusbpunyv">
              <path d="M6 10V2M6 2L2.5 5.5M6 2l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-286" data-mp="12vclt30xlo7j" />
            </svg>
          </span>
        </div>
      </div>
    </div>;
}